import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { LayoutDashboardIcon, ZapIcon, SmartphoneIcon, SettingsIcon, ArrowRightIcon } from 'lucide-react';
export function ThemesPage() {
  usePageSEO({
    canonical: '/themes',
    keywords: 'Shopify themes, custom Shopify themes, premium Shopify themes, ecommerce themes, Shopify theme development',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Themes',
      path: '/themes'
    }]),
    title: 'Themes | Design Flow Digitals',
    description: 'Browse premium Shopify themes by Design Flow Digitals. Fast, customizable, and conversion-optimized themes built for modern ecommerce brands.'
  });
  const features = [{
    icon: <LayoutDashboardIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1731" />,
    title: 'Elegant Layouts',
    desc: 'Premium, conversion-optimized designs that elevate your brand identity and build trust with customers.'
  }, {
    icon: <ZapIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1732" />,
    title: 'Fast Performance',
    desc: 'Clean, lightweight code ensuring lightning-fast load times to reduce bounce rates and improve SEO.'
  }, {
    icon: <SettingsIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1733" />,
    title: 'Flexible Customization',
    desc: 'Extensive theme settings allowing you to tailor every aspect of your store without touching code.'
  }, {
    icon: <SmartphoneIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1734" />,
    title: 'Mobile First Design',
    desc: 'Flawless shopping experiences across all devices, capturing the growing mobile commerce market.'
  }];
  const upcomingThemes = [{
    name: 'Luxe',
    style: 'Minimal & High-End',
    gradient: 'from-stone-800 to-black'
  }, {
    name: 'Minimal Pro',
    style: 'Clean & Modern',
    gradient: 'from-slate-800 to-black'
  }, {
    name: 'Starter Flow',
    style: 'Versatile & Fast',
    gradient: 'from-zinc-800 to-black'
  }];
  return <div className="pt-32 pb-20" data-id="element-1735">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-1736">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1737">
          <LayoutDashboardIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-1738" />
          <span className="text-sm font-medium text-[#F3E5AB]" data-id="element-1739">
            Shopify Themes
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-1740">Premium Themes</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-1741">
          Designed for modern ecommerce brands. Clean code. Lightning speed.
          Built to convert.
        </p>
      </section>

      {/* Philosophy / Features */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-1742">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1743">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1744">
            Our Theme Philosophy
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1745">
            We build themes that don't just look beautiful, but are engineered
            to maximize your store's performance and sales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1746">
          {features.map((feature, index) => <div key={index} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors duration-300" data-id="element-1747">
              <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-1748">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1749">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1750">{feature.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Coming Soon */}
      <section className="section-padding" data-id="element-1751">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1752">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1753">
            Coming Soon to the Theme Store
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-1754">
            Our themes are designed to meet the strict standards of the Shopify
            Theme Marketplace.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1755">
          {upcomingThemes.map((theme, index) => <div key={index} className="glass-card overflow-hidden border-white/10 group" data-id="element-1756">
              <div className={`aspect-[4/3] bg-gradient-to-br ${theme.gradient} relative flex items-center justify-center border-b border-white/5`} data-id="element-1757">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" data-id="element-1758" />
                <div className="text-center z-10" data-id="element-1759">
                  <p className="text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-2" data-id="element-1760">
                    Preview
                  </p>
                  <p className="text-white/50 font-bold text-2xl" data-id="element-1761">
                    Coming Soon
                  </p>
                </div>
              </div>
              <div className="p-6" data-id="element-1762">
                <div className="flex items-center justify-between mb-1" data-id="element-1763">
                  <h3 className="text-xl font-bold text-white" data-id="element-1764">{theme.name}</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold uppercase tracking-wider" data-id="element-1765">
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-1766" />{' '}
                    Coming Soon
                  </span>
                </div>
                <p className="text-gray-400 text-sm" data-id="element-1767">{theme.style}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1768">
        <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1769">
          Want a Custom Theme?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1770">
          We build bespoke Shopify themes tailored specifically to your brand's
          unique requirements.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1771">
          Discuss Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1772" />
        </Link>
      </section>
    </div>;
}